import { useMemo } from "react";
import {
  AlertTriangle,
  BarChart3,
  FileText,
  Lightbulb,
  ShieldCheck,
  User,
} from "lucide-react";
import { BlockLink, SchoolLink } from "@/components/DrilldownLink";
import {
  buildSchoolDeepDiveActions,
  buildSchoolHeroCopy,
  buildSchoolHeroStats,
  buildSchoolHotspotQueue,
  buildSchoolRankList,
  buildSchoolRankingTable,
  buildSchoolSignalCards,
  buildTransitionBars,
  buildTransitionDonut,
  fmt,
} from "@/utils/dropboxDerived";
import "./DropboxSchoolDeepDiveTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const SIGNAL_ICONS = {
  volume: BarChart3,
  errors: AlertTriangle,
  dropout: User,
  clean: ShieldCheck,
};

const ACTION_ICONS = {
  red: AlertTriangle,
  orange: User,
  green: ShieldCheck,
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const DropboxSchoolDeepDiveTab = ({ topSchools = [], transitionData = [] }) => {
  const heroStats = useMemo(() => buildSchoolHeroStats(topSchools), [topSchools]);
  const heroCopy = useMemo(() => buildSchoolHeroCopy(topSchools), [topSchools]);
  const signalCards = useMemo(() => buildSchoolSignalCards(topSchools), [topSchools]);
  const rankList = useMemo(() => buildSchoolRankList(topSchools, 8), [topSchools]);
  const hotspotQueue = useMemo(() => buildSchoolHotspotQueue(topSchools, 5), [topSchools]);
  const transitionBars = useMemo(() => buildTransitionBars(transitionData), [transitionData]);
  const transitionDonut = useMemo(() => buildTransitionDonut(transitionData), [transitionData]);
  const rankingTable = useMemo(() => buildSchoolRankingTable(topSchools), [topSchools]);
  const actions = useMemo(() => buildSchoolDeepDiveActions(topSchools), [topSchools]);

  return (
    <div className="dbx-schools-tab" data-testid="dropbox-school-deep-dive">
      <section className="dbx-hero-card" id="dbx-school-hero">
        <div className="dbx-school-hero-layout">
          <div className="dbx-school-spotlight">
            <div className="dbx-school-rank-row">
              <div className="dbx-rank-badge">
                <div>
                  <strong>#1</strong>
                  <span>Volume</span>
                </div>
              </div>
              <div className="dbx-school-copy">
                <h2 className="dashboard-kpi-hero-title">School Deep Dive Command Center</h2>
                <p>{heroCopy}</p>
              </div>
            </div>
            <div className="dbx-status-row">
              {heroStats.chips.map((chip) => (
                <span key={chip.text} className={`dbx-status-chip ${chip.className}`.trim()}>
                  {chip.text}
                </span>
              ))}
            </div>
            <div className="dbx-summary-note">
              <div className="dbx-icon-box purple">
                <FileText size={22} />
              </div>
              <div>
                <b>Primary review action</b>
                <span>
                  Use the school list to split work into two queues: high-volume clean schools for
                  validation and high-error schools for correction follow-up.
                </span>
              </div>
            </div>
          </div>

          <div className="dbx-school-metric-grid" aria-label="School deep dive KPIs">
            {heroStats.kpis.map((card) => (
              <article key={card.label} className="dbx-school-metric-card">
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span className={`dbx-metric-sub ${card.subClass}`.trim()}>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="dbx-section-title">
        <div>
          <h2>School-Level Signal Cards</h2>
          <p>
            Separate high-volume schools from high-risk schools so district officers can quickly
            decide whether to validate, correct, or investigate.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-school-table")}>
          Open full ranking →
        </button>
      </div>

      <section className="dbx-school-insight-grid" aria-label="School-level signal cards">
        {signalCards.map((card) => {
          const Icon = SIGNAL_ICONS[card.id] ?? BarChart3;
          return (
            <article key={card.id} className="dbx-school-insight-card">
              <div className="dbx-insight-head">
                <div className={`dbx-icon-box ${card.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`dbx-status-dot ${card.dotClass}`.trim()} />
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <div className="dbx-insight-score">
                <strong>{fmt(card.value)}</strong>
                <span>{card.unit}</span>
              </div>
              <div className="dbx-progress-line">
                <i style={{ width: `${card.barWidth}%`, background: card.barColor }} />
              </div>
              <div className="dbx-insight-meta">
                <span>{card.metaLeft}</span>
                <span>{card.metaRight}</span>
              </div>
            </article>
          );
        })}
      </section>

      <div className="dbx-section-title" id="dbx-diagnostics">
        <div>
          <h2>Prioritized School Diagnostics</h2>
          <p>
            Top schools are displayed as a digestible review queue before the full table, reducing
            the need to scan 20 dense rows immediately.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-transition")}>
          View transitions ↓
        </button>
      </div>

      <section className="dbx-school-analytics-grid">
        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Top Schools by Remarks Volume</h3>
              <p>Highest operational load schools with quick quality status.</p>
            </div>
            <span className="dbx-pill">Top 8</span>
          </div>
          <div className="dbx-school-rank-list" aria-label="Top schools by remarks volume">
            {rankList.map((row) => (
              <div key={row.school.udise_code || row.rank} className="dbx-school-rank-item">
                <div className="dbx-school-rank-number">{row.rank}</div>
                <div className="dbx-school-name">
                  <b>
                    <SchoolLink udiseCode={row.school.udise_code}>{row.school.school_name}</SchoolLink>
                  </b>
                  <span>
                    <BlockLink blockCode={row.school.block_code}>{row.school.block_name}</BlockLink>
                  </span>
                </div>
                <div className="dbx-school-rank-stat">
                  <b>{fmt(row.total)}</b>
                  <span>Total</span>
                </div>
                <span className={`dbx-quality-chip ${row.status.qualityClass}`.trim()}>
                  {row.status.chipText}
                </span>
              </div>
            ))}
          </div>
        </article>

        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Error and Dropout Hotspot Queue</h3>
              <p>Shows where correction and verification effort should start.</p>
            </div>
            <span className="dbx-pill red">Priority</span>
          </div>
          <div className="dbx-school-risk-list">
            {hotspotQueue.map((row) => (
              <div key={`${row.school.udise_code}-${row.type}`} className="dbx-school-risk-row">
                <div className="dbx-school-risk-label">
                  <b>{row.shortName}</b>
                  <span>
                    {row.block} · {row.reason}
                  </span>
                  <div className={`dbx-school-risk-meter ${row.meterClass}`.trim()}>
                    <i style={{ width: `${row.widthPct}%` }} />
                  </div>
                </div>
                <div className="dbx-school-risk-value">{fmt(row.value)}</div>
              </div>
            ))}
          </div>
          <div className="dbx-action-footer">
            <b>Recommended cut-off</b>
            <span>
              Create two review queues: schools with errors above 250 and schools with dropout
              remarks above 50. This keeps the school deep dive action-oriented.
            </span>
          </div>
        </article>
      </section>

      <div className="dbx-section-title" id="dbx-transition">
        <div>
          <h2>Student Transition Analysis</h2>
          <p>
            The long horizontal chart is converted into a ranked bar list and transition mix
            summary, making it easier to understand where students are moving.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-school-actions")}>
          Go to actions →
        </button>
      </div>

      <section className="dbx-school-transition-grid">
        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Where Students Are Transitioning</h3>
              <p>Ranked view of transition destinations from remarks categories.</p>
            </div>
            <span className="dbx-pill green">Category mix</span>
          </div>
          <div className="dbx-school-bar-list" aria-label="Student transition analysis">
            {transitionBars.map((row) => (
              <div key={row.label} className="dbx-school-hbar-row">
                <div className="dbx-school-hbar-label">{row.label}</div>
                <div className="dbx-school-hbar-track">
                  <div
                    className={`dbx-school-hbar ${row.barClass}`.trim()}
                    style={{ width: `${row.widthPct}%` }}
                  />
                </div>
                <div className="dbx-school-hbar-value">{row.displayValue}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Transition Mix Summary</h3>
              <p>
                Completion dominates, but domestic migration and alternative education pathways need
                continuous monitoring.
              </p>
            </div>
            <span className="dbx-pill">Share</span>
          </div>
          <div className="dbx-school-donut-layout">
            <div className="dbx-school-donut" style={transitionDonut.donutStyle}>
              <div>
                <strong>{transitionDonut.completionPct}%</strong>
                <span>Completion</span>
              </div>
            </div>
            <div className="dbx-school-legend">
              {transitionDonut.legend.map((item) => (
                <div key={item.label} className="dbx-school-legend-row">
                  <span>
                    <i className={`dbx-school-dot ${item.dotClass}`.trim()} />
                    {item.label}
                  </span>
                  <b>{item.pct}%</b>
                </div>
              ))}
            </div>
          </div>
          <div className="dbx-summary-note dbx-school-interpretation">
            <div className="dbx-icon-box green">
              <Lightbulb size={22} />
            </div>
            <div>
              <b>Interpretation</b>
              <span>
                The transition pattern is healthy overall, but the dropout and migration tails should
                be verified school-wise before block-level review meetings.
              </span>
            </div>
          </div>
        </article>
      </section>

      <div className="dbx-section-title" id="dbx-school-table">
        <div>
          <h2>Full School Ranking Table</h2>
          <p>
            The table is retained for operational drill-down, but moved below summaries so users
            first understand what needs attention.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-school-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="dbx-panel dbx-table-card">
        <div className="dbx-table-head">
          <div>
            <h3>Top 20 Schools by Remarks Volume</h3>
            <p>School-wise list with block, remarks, dropout, class 12 and error counts.</p>
          </div>
          <span className="dbx-pill">{rankingTable.length} schools</span>
        </div>
        <div className="dbx-school-table-scroll">
          <table className="dbx-overview-table dbx-school-table" aria-label="Top 20 schools by remarks volume">
            <thead>
              <tr>
                <th>#</th>
                <th>School Name</th>
                <th>Block</th>
                <th className="num">Total</th>
                <th className="num">Dropout</th>
                <th className="num">Class 12</th>
                <th className="num">Errors</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rankingTable.map((row) => (
                <tr key={row.school.udise_code || row.rank}>
                  <td>{row.rank}</td>
                  <td className="dbx-school-link">
                    <SchoolLink udiseCode={row.school.udise_code}>
                      {row.school.school_name}
                    </SchoolLink>
                  </td>
                  <td>
                    <BlockLink blockCode={row.school.block_code}>{row.school.block_name}</BlockLink>
                  </td>
                  <td className="num">{fmt(row.school.total_remarks)}</td>
                  <td className="num">{fmt(row.school.dropout)}</td>
                  <td className="num">{fmt(row.school.class12_passed)}</td>
                  <td className="num">{fmt(row.school.wrong_entry)}</td>
                  <td>
                    <span className={`dbx-tag ${row.status.tagClass}`.trim()}>{row.status.label}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="dbx-section-title" id="dbx-school-actions">
        <div>
          <h2>Recommended Action Queue</h2>
          <p>
            Turn school deep-dive insights into practical next steps for block officers and school
            administrators.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-school-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="dbx-school-two-column">
        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>School Follow-Up Workflow</h3>
              <p>Three focused workstreams for the current review cycle.</p>
            </div>
            <span className="dbx-pill amber">This week</span>
          </div>
          <div className="dbx-school-action-card">
            {actions.items.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? AlertTriangle;
              return (
                <div key={item.title} className="dbx-school-action-item">
                  <div className={`dbx-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Decision Rules</h3>
              <p>Simple rules make the dashboard easier to operationalize.</p>
            </div>
            <span className="dbx-pill">Rules</span>
          </div>
          <div className="dbx-school-bar-list">
            <div className="dbx-school-hbar-row">
              <div className="dbx-school-hbar-label">High Error</div>
              <div className="dbx-school-hbar-track">
                <div className="dbx-school-hbar red" style={{ width: "100%" }} />
              </div>
              <div className="dbx-school-hbar-value">&gt;250</div>
            </div>
            <div className="dbx-school-hbar-row">
              <div className="dbx-school-hbar-label">Dropout Review</div>
              <div className="dbx-school-hbar-track">
                <div className="dbx-school-hbar orange" style={{ width: "70%" }} />
              </div>
              <div className="dbx-school-hbar-value">&gt;50</div>
            </div>
            <div className="dbx-school-hbar-row">
              <div className="dbx-school-hbar-label">Clean Validation</div>
              <div className="dbx-school-hbar-track">
                <div className="dbx-school-hbar green" style={{ width: "55%" }} />
              </div>
              <div className="dbx-school-hbar-value">0–10</div>
            </div>
            <div className="dbx-school-hbar-row">
              <div className="dbx-school-hbar-label">Monitor</div>
              <div className="dbx-school-hbar-track">
                <div className="dbx-school-hbar" style={{ width: "35%" }} />
              </div>
              <div className="dbx-school-hbar-value">11–100</div>
            </div>
          </div>
          <div className="dbx-action-footer">
            <b>Next review recommendation</b>
            <span>{actions.footer}</span>
          </div>
        </article>
      </section>
    </div>
  );
};

export default DropboxSchoolDeepDiveTab;
